// signIn.tsx

import React, { useState } from 'react';
import styled from 'styled-components';
import { Input, Checkbox, Button, Typography, message, Modal } from 'antd';
import Cookies from 'js-cookie';
import { useGlobalContext } from '../../store/GlobalContext';
import { LoginResponse, login, forgotPassword } from './authservice';
import { useNavigate } from 'react-router-dom';

// Destructure Typography elements for easy access
const { Title, Text, Link } = Typography;

// Main Container to align form and side panel
const Container = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  padding: 2rem;
  align-items: center;
  justify-content: center;
  background-color: #f4f6fb;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

// Styled components
const LeftSide = styled.div`
  max-width: 45%;
  width: 100%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 2rem;
  @media (max-width: 768px) {
    max-width: 100%;
    margin-right: 0;
  }
`;

const SignInSection = styled.div`
  max-width: 400px;
  padding: 2rem;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const SidePanel = styled.div`
  display: none;
  @media (min-width: 768px) {
    flex: 1;
    padding: 3rem;
    height: 80vh;
    background-color: #3b82f6;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: #ffffff;
    border-radius: 12px;
  }
`;

const Logo = styled.img`
  width: 100px;
  margin-bottom: 1rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: 0.75rem;
  font-weight: bold;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const Footer = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  margin-top: 1rem;
`;

const SignInForm: React.FC = () => {
  const { setAuth } = useGlobalContext();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  // State and handlers for forgot password modal
  const [forgotModalVisible, setForgotModalVisible] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const openForgotModal = () => setForgotModalVisible(true);
  const closeForgotModal = () => {
    setForgotModalVisible(false);
    setForgotEmail('');
  };

  // Handle login
  const handleLogin = async () => {
    setLoading(true);
    try {
      const response: LoginResponse = await login(email, password);

      // Update global context with user and token
      setAuth({
        user: response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });

      // Clear old cookies
      Cookies.remove('accessToken');
      Cookies.remove('user');

      // Store token and user data in cookies
      const minimalUser = {
        id: response.user.id,
        uid: response.user.uid,
        username: response.user.firstName,
        email: response.user.email,
      };

      Cookies.set('accessToken', response.accessToken, {
        expires: rememberMe ? 30 : undefined,
      });
      Cookies.set('user', JSON.stringify(minimalUser), {
        expires: rememberMe ? 30 : undefined,
      });
      Cookies.set('refreshToken', response.refreshToken, {
        expires: rememberMe ? 30 : undefined,
      });

      message.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      message.error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    setForgotLoading(true);
    try {
      // Call forgotPassword service
      await forgotPassword(forgotEmail);

      message.success(
        'Password reset instructions have been sent to your email. Please check your inbox (and spam folder).'
      );
      closeForgotModal();
    } catch (error) {
      message.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      <Container>
        <LeftSide>
          <Logo src="/logo.png" alt="Logo" />
          <SignInSection>
            <Title level={2}>Sign In</Title>
            <Text type="secondary">Welcome back! Please enter your details</Text>
            <Text strong>Email</Text>
            <Input
              placeholder="Enter your email"
              type="email"
              style={{ marginBottom: '1rem' }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Text strong>Password</Text>
            <Input.Password
              placeholder="Password"
              style={{ marginBottom: '1rem' }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <CheckboxContainer>
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              >
                Remember for 30 Days
              </Checkbox>
            </CheckboxContainer>
            {/* Forgot Password Link: triggers modal */}
            <Link href="#" style={{ marginBottom: '1.5rem' }} onClick={openForgotModal}>
              Forgot password?
            </Link>
            <StyledButton
              type="primary"
              size="large"
              style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
              loading={loading}
              onClick={handleLogin}
            >
              Sign In
            </StyledButton>
            <Footer>
              Don’t have an account? <Link href="signup">Sign up</Link>
            </Footer>
          </SignInSection>
        </LeftSide>

        {/* Side Panel */}
        <SidePanel>
          <Title level={2} style={{ color: '#ffffff' }}>
            Welcome back to Wzee!
          </Title>
          <Text style={{ color: '#ffffff', lineHeight: 1.6 }}>
            Welcome to Wzee. We help you manage your estate and assets seamlessly
            and effortlessly. We guide you through each step and make the
            experience magnitudes easier by automating the processes that are
            largely manual.
          </Text>
        </SidePanel>
      </Container>

      {/* Forgot Password Modal */}
      <Modal
        title="Reset Password"
        open={forgotModalVisible}
        onCancel={closeForgotModal}
        footer={null}
      >
        <Text>
          Please enter the email associated with your account. We will send you
          a reset link.
        </Text>
        <Input
          placeholder="Enter your email"
          type="email"
          style={{ margin: '1rem 0' }}
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
        />
        <Button
          type="primary"
          block
          loading={forgotLoading}
          onClick={handleForgotPassword}
        >
          Submit
        </Button>
      </Modal>
    </>
  );
};

export default SignInForm;
